import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import {
  appInnerCardStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { normalizePlainText, sanitizeRichHtml } from "../../../utils/securityUtils";
import Unauthorized from "../../../shared/pages/Unauthorized";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import DailyBehaviorLogPanel from "../components/DailyBehaviorLogPanel";
import ResidentDailyLogImportDialog from "../components/ResidentDailyLogImportDialog";
import { buildBehaviorPrnMedicationOptions, buildBehaviorPrnSyncPayload } from "../utils/behaviorPrnUtils";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const DAILY_BEHAVIOR_IMPORT_ACCEPT = ".doc,.docx,.txt,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const DAILY_BEHAVIOR_IMPORT_ALLOWED_EXTENSIONS = ["doc", "docx", "txt"];
const DAILY_BEHAVIOR_IMPORT_MAX_FILE_SIZE_IN_BYTES = 10 * 1024 * 1024;

const escapeBehaviorReportHtml = (value: any) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getDefaultCbhsMonth = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return year && month ? `${year}-${month}` : "";
};

const getCbhsMonthDateValue = (monthKey?: string) => {
  const normalizedMonthKey = String(monthKey || "").trim();
  if (!/^\d{4}-\d{2}$/.test(normalizedMonthKey)) {
    return null;
  }

  const [year, month] = normalizedMonthKey.split("-").map((value) => Number(value));
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, 1, 12, 0, 0));
};

const getCbhsMonthKeyFromDateValue = (value?: Date | Date[] | null) => {
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  if (!(resolvedValue instanceof Date) || Number.isNaN(resolvedValue.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(resolvedValue);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return year && month ? `${year}-${month}` : "";
};

const BEHAVIOR_REPORT_RANGE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7Days" },
  { label: "Monthly", value: "monthly" },
] as const;
const CUSTOM_BEHAVIOR_KEY = "__custom_behavior__";

const getDefaultDateInputValue = () => {
  const currentDate = new Date();
  const offsetDate = new Date(currentDate.getTime() - (currentDate.getTimezoneOffset() * 60000));
  return offsetDate.toISOString().slice(0, 10);
};

const getPrintableUserName = (profile: any) =>
  String(
    `${String(profile?.firstName || "").trim()} ${String(profile?.lastName || "").trim()}`.trim()
    || profile?.name
    || "",
  ).trim();

const getResidentDobFromCarePlanSummary = (carePlanData: any) =>
  String(
    carePlanData?.sections?.SUMMARY?.step01?.residentBirthdate
    || carePlanData?.sections?.SUMMARY?.residentBirthdate
    || "",
  ).trim();

const normalizeDateInputValue = (value?: string) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return normalizedValue;
  }

  const slashDateMatch = normalizedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDateMatch) {
    const [, month, day, year] = slashDateMatch;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toISOString().slice(0, 10);
};

const formatCbhsDayLabel = (dateKey: string, weekdayLabel?: string) => {
  const parsedDate = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateKey || "Day";
  }

  const fallbackWeekday = parsedDate.toLocaleString("en-US", { weekday: "short" });
  return `${weekdayLabel || fallbackWeekday} ${parsedDate.getDate()}`;
};

const formatBehaviorHours = (value: any) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "0";
  }

  return numericValue.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};

const CBHS_TIER_LEGEND = [
  "Tier 1: 0.5 to 2.0 hours per day",
  "Tier 2: 2.1 to 6.0 hours per day",
  "Tier 3: 6.1 to 10.0 hours per day",
  "Tier 4: 10.1 to 15.0 hours per day",
  "Tier 5: 15.1 to 20.0 hours per day",
  "Tier 6: 20.1 to 24.0 hours per day",
];
const CBHS_MAX_BEHAVIORS_PER_TABLE = 20;
const chunkBehaviorSlots = (slots: any[]) => Array.from({ length: Math.ceil(slots.length / CBHS_MAX_BEHAVIORS_PER_TABLE) }, (_, index) => slots.slice(index * CBHS_MAX_BEHAVIORS_PER_TABLE, (index + 1) * CBHS_MAX_BEHAVIORS_PER_TABLE));
const buildCbhsBehaviorDailyTableHtml = (week: any, behaviorSlots: any[]) => {
  const slotHeaders = behaviorSlots.map((slot: any) => `<th>${escapeBehaviorReportHtml(String(slot?.slotNumber || ""))}</th>`).join("");
  const dayRows = (week?.days || []).map((day: any) => `
    <tr>
      <td>${escapeBehaviorReportHtml(formatCbhsDayLabel(String(day?.dateKey || ""), String(day?.weekdayLabel || "")))}</td>
      <td>${escapeBehaviorReportHtml((day?.servicePeriods || []).join(", ") || "-")}</td>
      <td>${escapeBehaviorReportHtml((day?.caregiverInitials || []).join(", ") || "-")}</td>
      ${behaviorSlots.map((slot: any) => {
    const occurrence = (day?.behaviorOccurrences || []).find((item: any) => String(item?.behaviorKey || "") === String(slot?.behaviorKey || ""));
    return `<td>${escapeBehaviorReportHtml(String(occurrence?.count || 0))}</td>`;
  }).join("")}
    </tr>
  `).join("");
  return `<table><thead><tr><th>Date</th><th>Service Periods</th><th>Staff Initials</th>${slotHeaders}</tr></thead><tbody>${dayRows}</tbody></table>`;
};

const buildCbhsBehaviorPacketHtml = (packet: any) => {
  const behaviorSlots = Array.isArray(packet?.behaviorSlots) ? packet.behaviorSlots : [];

  const slotSummaryRows = behaviorSlots
    .map((slot: any) => `
      <tr>
        <td>${escapeBehaviorReportHtml(String(slot?.slotNumber || ""))}</td>
        <td>${escapeBehaviorReportHtml(String(slot?.behaviorTitle || ""))}</td>
        <td>${escapeBehaviorReportHtml(String(slot?.standardIntervention || ""))}</td>
        <td>${escapeBehaviorReportHtml(String(slot?.occurrenceCount || 0))}</td>
        <td>${escapeBehaviorReportHtml(formatBehaviorHours(slot?.totalHours))}</td>
      </tr>
    `)
    .join("");

  const weeklyBehaviorHoursHtml = (week: any) => {
    const weeklyBehaviorHours = (week?.totalHoursByBehavior || []).filter((item: any) => Number(item?.totalHours || 0) > 0);
    if (!weeklyBehaviorHours.length) {
      return "";
    }

    return `
      <div style="margin-top: 12px;">
        <div style="font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 6px;">Weekly Total Hours by Behavior</div>
        <table>
          <thead>
            <tr>
              <th>Slot</th>
              <th>Behavior</th>
              <th>Total Hours</th>
            </tr>
          </thead>
          <tbody>
            ${weeklyBehaviorHours.map((item: any) => `
              <tr>
                <td>${escapeBehaviorReportHtml(String(item?.slotNumber || ""))}</td>
                <td>${escapeBehaviorReportHtml(String(item?.behaviorTitle || ""))}</td>
                <td>${escapeBehaviorReportHtml(formatBehaviorHours(item?.totalHours))}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;
  };

  const weeksHtml = (packet?.weeks || [])
    .map((week: any) => {
      const behaviorTables = chunkBehaviorSlots(behaviorSlots)
        .map((slotGroup: any[], index: number) => `<div class="behavior-table-block">${behaviorSlots.length > CBHS_MAX_BEHAVIORS_PER_TABLE ? `<div class="behavior-table-label">Behavior table ${index + 1}</div>` : ""}${buildCbhsBehaviorDailyTableHtml(week, slotGroup)}</div>`)
        .join("");

      return `
        <div class="week-block">
          <h3>Week ${escapeBehaviorReportHtml(String(week?.weekNumber || ""))}</h3>
          ${behaviorTables}
          ${weeklyBehaviorHoursHtml(week)}
          <div class="week-summary-header">Weekly ${escapeBehaviorReportHtml(String(week?.weekNumber || ""))} Summary: Note behaviors, unusual events, interventions, and whether they were successful.</div>
          <div class="week-summary">${sanitizeRichHtml(String(week?.summary || ""))}</div>
        </div>
      `;
    })
    .join("");

  return `
    <section class="packet-section">
      <h2>${escapeBehaviorReportHtml(String(packet?.packetLabel || ""))}</h2>
      <table>
        <thead>
          <tr>
            <th>Slot</th>
            <th>Behavior</th>
            <th>Standard Intervention</th>
            <th>Monthly Occurrences</th>
            <th>Monthly Total Hours</th>
          </tr>
        </thead>
        <tbody>${slotSummaryRows}</tbody>
      </table>
      ${weeksHtml}
    </section>
  `;
};

const getCbhsWeekSummaryScope = (dataset: any) => {
  const rangeKey = String(dataset?.reportRangeKey || "monthly").trim() || "monthly";
  const rangeLabel = String(
    dataset?.monthKey
    || dataset?.reportLabel
    || dataset?.monthLabel
    || "",
  ).trim();
  return `${rangeKey}:${rangeLabel}`;
};

const getCbhsWeekSummaryKey = (scopeKey: string, packetIndex: number, weekNumber: number) => `${scopeKey}:${packetIndex}-${weekNumber}`;

const readFileAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      const result = String(fileReader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    fileReader.onerror = () => reject(new Error("Unable to read the selected file."));
    fileReader.readAsDataURL(file);
  });

type ProviderCbhsSection = "composer" | "monthly" | "recent" | "drafts" | "import";

const resolveProviderCbhsSectionFromQuery = (value?: string): ProviderCbhsSection | null => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) {
    return null;
  }

  if (["new", "new-note", "new_note", "note", "composer"].includes(normalizedValue)) {
    return "composer";
  }

  if (["monthly", "monthly-report", "monthly_report", "report"].includes(normalizedValue)) {
    return "monthly";
  }

  if (["recent", "recent-reports", "recent_reports", "reports"].includes(normalizedValue)) {
    return "recent";
  }

  if (["draft", "drafts", "draft-report", "draft-reports", "draft_reports"].includes(normalizedValue)) {
    return "drafts";
  }

  if (["import", "import-progress-notes", "import_progress_notes", "progress-notes-import"].includes(normalizedValue)) {
    return "import";
  }

  return null;
};

const buildCbhsSummaryDrafts = (dataset: any) => {
  const drafts: Record<string, string> = {};
  const scopeKey = getCbhsWeekSummaryScope(dataset);
  (dataset?.packets || []).forEach((packet: any) => {
    const packetIndex = Number(packet?.packetIndex || 0);
    (packet?.weeks || []).forEach((week: any) => {
      drafts[getCbhsWeekSummaryKey(scopeKey, packetIndex, Number(week?.weekNumber || 0))] = String(week?.summary || "");
    });
  });
  return drafts;
};

const applyCbhsSummaryDrafts = (dataset: any, drafts: Record<string, string>) => {
  if (!dataset) {
    return dataset;
  }

  const scopeKey = getCbhsWeekSummaryScope(dataset);
  return {
    ...dataset,
    packets: (dataset?.packets || []).map((packet: any) => ({
      ...packet,
      weeks: (packet?.weeks || []).map((week: any) => ({
        ...week,
        summary: drafts[getCbhsWeekSummaryKey(scopeKey, Number(packet?.packetIndex || 0), Number(week?.weekNumber || 0))]
          ?? String(week?.summary || ""),
      })),
    })),
  };
};

const updateCbhsWeekSummaryDraft = (
  summaryKey: string,
  nextHtml: string,
  setDrafts: any,
) => {
  const sanitizedSummaryHtml = sanitizeRichHtml(nextHtml || "");
  setDrafts((previousState) => ({
    ...previousState,
    [summaryKey]: sanitizedSummaryHtml,
  }));
};

const ProviderDailyBehaviorLogPage = () => {
  const { residentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const homeId = String(searchParams.get("homeId") || "").trim();
  const toast = useRef<Toast>(null);

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const [loading, setLoading] = useState<boolean>(false);
  const [carePlanData, setCarePlanData] = useState<any>(null);
  const [residentBehaviorSnapshot, setResidentBehaviorSnapshot] = useState<any>(null);
  const [isProviderSubscribed, setIsProviderSubscribed] = useState<boolean>(true);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false);
  const [residentName, setResidentName] = useState<string>("Resident");
  const [residentProfileData, setResidentProfileData] = useState<any>(null);
  const [facilityType, setFacilityType] = useState<string>("");
  const resolvedFacilityType = facilityType
    || String(residentProfileData?.facilityType || "").trim().toLowerCase()
    || String(authStore.getUserFacilityType() || "").trim().toLowerCase();
  const allowRecentEntryReuse = authExperience.featureFlags.dailyBehaviorLogReuseRecentEntries.enabled
    && !authExperience.featureFlags.dailyBehaviorLogReuseRecentEntries.excludedFacilityTypes.includes(resolvedFacilityType);
  const archivedReadOnly = searchParams.get("archived") === "true" && residentProfileData && residentProfileData.active === false;
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [behaviorLogEntries, setBehaviorLogEntries] = useState<any[]>([]);
  const [behaviorLogPage, setBehaviorLogPage] = useState<number>(1);
  const [behaviorLogPageTokens, setBehaviorLogPageTokens] = useState<string[]>([""]);
  const [behaviorLogNextToken, setBehaviorLogNextToken] = useState<string>("");
  const [loadingBehaviorLogEntries, setLoadingBehaviorLogEntries] = useState<boolean>(false);
  const [savingBehaviorLogEntry, setSavingBehaviorLogEntry] = useState<boolean>(false);
  const [loadingPrnMedicationOptions, setLoadingPrnMedicationOptions] = useState<boolean>(false);
  const [prnMedicationOptions, setPrnMedicationOptions] = useState<Array<{ label: string; value: string; medicationName?: string }>>([]);
  const resolvedSectionFromQuery = resolveProviderCbhsSectionFromQuery(searchParams.get("section") || "");
  const defaultActiveSection = resolvedSectionFromQuery === "import" ? "recent" : (resolvedSectionFromQuery || "composer");
  const [activeSection, setActiveSection] = useState<ProviderCbhsSection>(defaultActiveSection);
  const [selectedCbhsMonth, setSelectedCbhsMonth] = useState<string>(getDefaultCbhsMonth());
  const [selectedBehaviorReportRange, setSelectedBehaviorReportRange] = useState<"today" | "last7Days" | "monthly">("monthly");
  const [cbhsPreviewDataset, setCbhsPreviewDataset] = useState<any>(null);
  const [cbhsWeekSummaryDrafts, setCbhsWeekSummaryDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    setActiveSection(defaultActiveSection);
  }, [defaultActiveSection]);

  useEffect(() => {
    if (archivedReadOnly) {
      setActiveSection("monthly");
    }
  }, [archivedReadOnly]);

  useEffect(() => {
    const currentSection = String(searchParams.get("section") || "").trim();
    if (currentSection === activeSection) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("section", activeSection);
    setSearchParams(nextSearchParams, { replace: true });
  }, [activeSection, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeSection !== "import") {
      return;
    }
    setShowSharedDailyLogImportDialog(true);
    setActiveSection("recent");
  }, [activeSection]);

  const [loadingCbhsPreview, setLoadingCbhsPreview] = useState<boolean>(false);
  const [generatingCbhsReport, setGeneratingCbhsReport] = useState<boolean>(false);
  const [showProviderOneIdDialog, setShowProviderOneIdDialog] = useState<boolean>(false);
  const [providerOneIdDraft, setProviderOneIdDraft] = useState<string>("");
  const [savingProviderOneId, setSavingProviderOneId] = useState<boolean>(false);
  const [showResidentDobDialog, setShowResidentDobDialog] = useState<boolean>(false);
  const [residentDobDraft, setResidentDobDraft] = useState<string>("");
  const [savingResidentDob, setSavingResidentDob] = useState<boolean>(false);
  const [reportProviderOneId, setReportProviderOneId] = useState<string>("");
  const [reportResidentDob, setReportResidentDob] = useState<string>("");
  const [reportPrintedName, setReportPrintedName] = useState<string>("");
  const [reportSignatureDate, setReportSignatureDate] = useState<string>(getDefaultDateInputValue());
  const [pendingReportOutputMode, setPendingReportOutputMode] = useState<"print" | "view">("view");
  const [behaviorReportIncludesTierDetails, setBehaviorReportIncludesTierDetails] = useState<boolean>(true);
  const [behaviorHoursMode, setBehaviorHoursMode] = useState<"common" | "separate">("common");
  const [showBehaviorReportPreviewDialog, setShowBehaviorReportPreviewDialog] = useState<boolean>(false);
  const [behaviorReportPreviewHtml, setBehaviorReportPreviewHtml] = useState<string>("");
  const behaviorReportPreviewFrameRef = useRef<HTMLIFrameElement>(null);
  const [showMissingCarePlanDialog, setShowMissingCarePlanDialog] = useState<boolean>(false);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedImportFile, setSelectedImportFile] = useState<File | null>(null);
  const [previewingImportFile, setPreviewingImportFile] = useState<boolean>(false);
  const [savingImportedBehaviorEntries, setSavingImportedBehaviorEntries] = useState<boolean>(false);
  const [behaviorImportPreview, setBehaviorImportPreview] = useState<any>(null);
  const [showSharedDailyLogImportDialog, setShowSharedDailyLogImportDialog] = useState<boolean>(false);

  useEffect(() => {
    const loadResidentContext = async () => {
      if (!residentId || (userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser)) {
        return;
      }

      setLoading(true);
      try {
        await backendAPIStore.init();
        const subscriptionDetails = isAdminUser
          ? { isSubscribed: true }
          : await backendAPIStore.getSubscription(authStore.getUserId(), ROLE_TYPE_PROVIDER);
        const subscribed = isAdminUser || !!subscriptionDetails?.isSubscribed;
        setIsProviderSubscribed(subscribed);
        if (!subscribed) {
          setShowSubscriptionDialog(true);
          setCarePlanData(null);
          setResidentBehaviorSnapshot(null);
          setResidentProfileData(null);
          setCurrentUserProfile(null);
          return;
        }
        const [carePlanResponse, residentBehaviorSnapshotResponse, residentProfileResponse, userProfileResponse, homeProfileResponse] = await Promise.all([
          backendAPIStore.getResidentCarePlanBundle(residentId),
          backendAPIStore.getResidentBehaviorSnapshot(residentId),
          backendAPIStore.getProviderResidentProfile(residentId),
          backendAPIStore.getUserProfile(authStore.getUserId()),
          homeId ? backendAPIStore.getBusinessHomeDetails(homeId) : Promise.resolve(null),
        ]);

        setCarePlanData(carePlanResponse || null);
        setResidentBehaviorSnapshot(residentBehaviorSnapshotResponse || null);
        setResidentProfileData(residentProfileResponse || null);
        setFacilityType(String(homeProfileResponse?.facilityType || residentProfileResponse?.facilityType || "").trim().toLowerCase());
        setCurrentUserProfile(userProfileResponse || null);
        setProviderOneIdDraft(String(userProfileResponse?.providerOneId || "").trim());
        const resolvedResidentDob = normalizeDateInputValue(
          getResidentDobFromCarePlanSummary(carePlanResponse)
          || String(residentProfileResponse?.dateOfBirth || "").trim(),
        );
        setResidentDobDraft(resolvedResidentDob);
        setReportProviderOneId(String(userProfileResponse?.providerOneId || "").trim());
        setReportResidentDob(resolvedResidentDob);
        setReportPrintedName(getPrintableUserName(userProfileResponse));

        const resolvedResidentName = [
          String(carePlanResponse?.residentName || "").trim(),
          `${residentProfileResponse?.firstName || ""} ${residentProfileResponse?.lastName || ""}`.trim(),
        ].find((value) => Boolean(value)) || "Resident";

        setResidentName(resolvedResidentName);
      } catch (error: any) {
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setIsProviderSubscribed(false);
          setShowSubscriptionDialog(true);
          setCarePlanData(null);
          setResidentBehaviorSnapshot(null);
        } else {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to load resident behavior details.",
            life: 4000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    void loadResidentContext();
  }, [backendAPIStore, homeId, residentId, userRoleType]);

  const reloadBehaviorLogEntries = async (options?: { page?: number; nextToken?: string; resetPagination?: boolean }) => {
    if (!residentId || (userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser)) {
      return;
    }
    if (!isAdminUser && !isProviderSubscribed) {
      return;
    }

    const targetPage = options?.resetPagination ? 1 : Math.max(1, options?.page || behaviorLogPage);
    const targetToken = options?.resetPagination ? "" : (options?.nextToken ?? behaviorLogPageTokens[targetPage - 1] ?? "");

    setLoadingBehaviorLogEntries(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getResidentBehaviorLogEntriesPage(residentId, {
        limit: 10,
        nextToken: targetToken,
      });
      const nextPageToken = String(response?.nextToken || "");
      setBehaviorLogEntries(Array.isArray(response?.items) ? response.items : []);
      setBehaviorLogPage(targetPage);
      setBehaviorLogNextToken(nextPageToken);
      setBehaviorLogPageTokens((previousState) => {
        if (options?.resetPagination) {
          return [""];
        }
        const nextState = previousState.slice(0, targetPage);
        nextState[targetPage - 1] = targetToken;
        return nextState;
      });
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_REQUIRED") {
        setIsProviderSubscribed(false);
        setShowSubscriptionDialog(true);
      } else {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to load daily behavior log.",
          life: 4000,
        });
      }
    } finally {
      setLoadingBehaviorLogEntries(false);
    }
  };

  useEffect(() => {
    void reloadBehaviorLogEntries();
  }, [residentId, userRoleType]);

  useEffect(() => {
    const loadPrnMedicationOptions = async () => {
      if (!residentId || (userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser)) {
        setPrnMedicationOptions([]);
        return;
      }
      setLoadingPrnMedicationOptions(true);
      try {
        const medicationResponse = await backendAPIStore.getResidentEmarMedications(residentId);
        setPrnMedicationOptions(buildBehaviorPrnMedicationOptions(medicationResponse?.items || medicationResponse || []));
      } catch {
        setPrnMedicationOptions([]);
      } finally {
        setLoadingPrnMedicationOptions(false);
      }
    };

    void loadPrnMedicationOptions();
  }, [backendAPIStore, isAdminUser, residentId, userRoleType]);

  useEffect(() => {
    const loadCbhsPreviewDataset = async () => {
      if (
        activeSection !== "monthly"
        || !residentId
        || (userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser)
        || (selectedBehaviorReportRange === "monthly" && !selectedCbhsMonth)
      ) {
        setCbhsPreviewDataset(null);
        setCbhsWeekSummaryDrafts({});
        return;
      }

      setLoadingCbhsPreview(true);
      try {
        await backendAPIStore.init();
        const dataset = await backendAPIStore.getResidentBehaviorLogCbhsMonthlyDataset(residentId, {
          reportRange: selectedBehaviorReportRange,
          reportMonth: selectedBehaviorReportRange === "monthly" ? selectedCbhsMonth : undefined,
          behaviorHoursMode,
        });
        setCbhsPreviewDataset(dataset || null);
        setCbhsWeekSummaryDrafts(buildCbhsSummaryDrafts(dataset));
        const datasetResidentDob = normalizeDateInputValue(String(dataset?.resident?.dateOfBirth || "").trim());
        if (datasetResidentDob && (!reportResidentDob || reportResidentDob === String(residentProfileData?.dateOfBirth || "").trim())) {
          setReportResidentDob(datasetResidentDob);
        }
      } catch (error: any) {
        setCbhsPreviewDataset(null);
        setCbhsWeekSummaryDrafts({});
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setIsProviderSubscribed(false);
          setShowSubscriptionDialog(true);
        } else {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to load the Behavior Tracker preview.",
            life: 4000,
          });
        }
      } finally {
        setLoadingCbhsPreview(false);
      }
    };

    void loadCbhsPreviewDataset();
  }, [activeSection, backendAPIStore, behaviorHoursMode, residentId, selectedCbhsMonth, selectedBehaviorReportRange, userRoleType, behaviorLogEntries]);

  const behaviorOptions = useMemo(() => {
    const behaviorRows = Array.isArray(residentBehaviorSnapshot?.behaviors)
      ? residentBehaviorSnapshot.behaviors
      : [];
    const optionMap = new Map<string, {
      behaviorKey: string;
      behaviorTitle: string;
      sectionCategory?: string;
      presentBehaviorSuggestions: string[];
      instructionSuggestions: string[];
      carePlanDetails?: string;
      caregiverInstructions?: string;
      carePlanInstructions?: string;
    }>();

    behaviorRows
      .filter((row: any) => row?.behaviorKey)
      .forEach((row: any) => {
        const behaviorKey = String(row?.behaviorKey || "").trim();
        if (!behaviorKey) {
          return;
        }

        const existingOption = optionMap.get(behaviorKey);
        const presentBehaviorText = String(
          row?.residentDetails || row?.description || row?.present || "",
        ).trim();
        const carePlanInstructionText = String(row?.carePlanInstructions || "").trim();
        const instructionText = [
          carePlanInstructionText,
          String(row?.caregiverInstructions || row?.instructions || "").trim(),
        ].filter(Boolean).join("\n");

        if (!existingOption) {
          optionMap.set(behaviorKey, {
            behaviorKey,
            behaviorTitle: String(row?.behaviorTitle || row?.itemLabel || "").trim() || "Behavior",
            sectionCategory: String(row?.sectionCategory || row?.sectionLabel || "").trim() || "Psych/Social & Cognitive",
            presentBehaviorSuggestions: presentBehaviorText ? [presentBehaviorText] : [],
            instructionSuggestions: instructionText ? [instructionText] : [],
            carePlanInstructions: carePlanInstructionText,
            carePlanDetails: presentBehaviorText,
            caregiverInstructions: instructionText,
          });
          return;
        }

        if (presentBehaviorText) {
          existingOption.presentBehaviorSuggestions.push(presentBehaviorText);
          if (!existingOption.carePlanDetails) {
            existingOption.carePlanDetails = presentBehaviorText;
          }
        }
        if (instructionText) {
          existingOption.instructionSuggestions.push(instructionText);
          if (!existingOption.caregiverInstructions) {
            existingOption.caregiverInstructions = instructionText;
          }
          if (carePlanInstructionText && !existingOption.carePlanInstructions) {
            existingOption.carePlanInstructions = carePlanInstructionText;
          }
        }
      });

    optionMap.forEach((option) => {
      const suggestions = option.instructionSuggestions
        .flatMap((value) => String(value || "").split(/\r?\n|[;]+/).map((item) => item.trim()).filter(Boolean));
      option.instructionSuggestions = [
        ...suggestions.filter((suggestion) => !/^Staff\b/i.test(suggestion)),
        ...suggestions.filter((suggestion) => /^Staff\b/i.test(suggestion)),
      ];
    });

    return [
      ...Array.from(optionMap.values()).sort((first, second) => first.behaviorTitle.localeCompare(second.behaviorTitle)),
      {
        behaviorKey: CUSTOM_BEHAVIOR_KEY,
        behaviorTitle: "Custom Behavior",
        sectionCategory: "Custom",
        presentBehaviorSuggestions: [],
        instructionSuggestions: [],
        carePlanDetails: "",
        caregiverInstructions: "",
      },
    ];
  }, [residentBehaviorSnapshot]);
  const hasBehaviorRows = behaviorOptions.length > 0;
  const canOpenManageCarePlan = Boolean(residentProfileData?.homeId) && Boolean(residentId);

  useEffect(() => {
    if (!carePlanData) {
      setShowMissingCarePlanDialog(false);
      return;
    }

    if (!hasBehaviorRows) {
      setShowMissingCarePlanDialog(true);
      if (activeSection === "composer") {
        setActiveSection("recent");
      }
      return;
    }

    setShowMissingCarePlanDialog(false);
  }, [activeSection, carePlanData, hasBehaviorRows]);

  const createBehaviorLogEntry = async (payload: Record<string, any>) => {
    if (archivedReadOnly) return;
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const resolvedPayload = await buildBehaviorPrnSyncPayload({
        backendAPIStore,
        residentId,
        behaviorPayload: payload,
      });
      await backendAPIStore.createResidentBehaviorLogEntry(residentId, resolvedPayload);
      await reloadBehaviorLogEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Behavior entry saved",
        detail: "Daily behavior log updated successfully.",
        life: 2500,
      });
      if (String(payload?.status || "").trim().toLowerCase() !== "draft") {
        setActiveSection("recent");
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save behavior entry.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const updateBehaviorLogEntry = async (behaviorLogEntryId: string, payload: Record<string, any>) => {
    if (archivedReadOnly) return;
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const resolvedPayload = await buildBehaviorPrnSyncPayload({
        backendAPIStore,
        residentId,
        behaviorPayload: payload,
      });
      await backendAPIStore.updateResidentBehaviorLogEntry(residentId, {
        behaviorLogEntryId,
        ...resolvedPayload,
      });
      await reloadBehaviorLogEntries();
      toast.current?.show({
        severity: "success",
        summary: "Behavior entry updated",
        detail: "Daily behavior log updated successfully.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to update behavior entry.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const deleteBehaviorLogDraft = async (behaviorLogEntryId: string) => {
    if (archivedReadOnly) return;
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      await backendAPIStore.deleteResidentBehaviorLogEntry(residentId, behaviorLogEntryId);
      await reloadBehaviorLogEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Draft deleted",
        detail: "Saved draft removed successfully.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to delete draft.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const saveWeeklySummaryDraft = (summaryKey: string) => {
    setCbhsWeekSummaryDrafts((previousState) => ({ ...previousState }));
    toast.current?.show({
      severity: "success",
      summary: "Weekly summary saved",
      detail: "Your changes will be used in the Behavior Tracker report.",
      life: 2000,
    });
  };

  const formatBehaviorLogText = async (requestBody: {
    text: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
    metadata?: Record<string, any>;
  }) => {
    try {
      return await backendAPIStore.formatAiText(requestBody);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to polish text with AI.",
        life: 4000,
      });
      throw error;
    }
  };

  const generateCaregiverInterventions = async (request: {
    behaviorTitle: string;
    behaviorCategory?: string;
    behaviorDetails?: string;
  }) => formatBehaviorLogText({
    text: "",
    taskType: "professional-note-cleanup",
    tone: "professional",
    domain: "behavior-caregiver-interventions",
    outputStyle: "plain_text",
    metadata: request,
  });

  const handleImportFileSelection = (event: any) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      return;
    }

    const extension = file.name.includes(".")
      ? file.name.split(".").pop()?.toLowerCase() || ""
      : "";
    if (!DAILY_BEHAVIOR_IMPORT_ALLOWED_EXTENSIONS.includes(extension)) {
      toast.current?.show({
        severity: "error",
        summary: "Invalid file",
        detail: "Please choose a DOC, DOCX, or TXT file.",
        life: 4000,
      });
      event.target.value = "";
      setSelectedImportFile(null);
      return;
    }

    if (file.size > DAILY_BEHAVIOR_IMPORT_MAX_FILE_SIZE_IN_BYTES) {
      toast.current?.show({
        severity: "error",
        summary: "File too large",
        detail: "The uploaded file must be 10 MB or smaller.",
        life: 4000,
      });
      event.target.value = "";
      setSelectedImportFile(null);
      return;
    }

    setSelectedImportFile(file);
    setBehaviorImportPreview(null);
  };

  const clearBehaviorImportState = () => {
    setSelectedImportFile(null);
    setBehaviorImportPreview(null);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = "";
    }
  };

  const previewBehaviorImport = async () => {
    if (!residentId || !selectedImportFile) {
      return;
    }

    setPreviewingImportFile(true);
    try {
      await backendAPIStore.init();
      const fileContentBase64 = await readFileAsBase64(selectedImportFile);
      const previewResponse = await backendAPIStore.previewResidentBehaviorLogImport(residentId, {
        fileName: selectedImportFile.name,
        mimeType: selectedImportFile.type,
        fileContentBase64,
      });
      setBehaviorImportPreview(previewResponse || null);
      toast.current?.show({
        severity: "success",
        summary: "Import preview ready",
        detail: "Review the matched entries before saving them.",
        life: 2500,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to preview the uploaded progress notes.",
        life: 5000,
      });
    } finally {
      setPreviewingImportFile(false);
    }
  };

  const importPreviewEntries = async () => {
    if (!residentId || !behaviorImportPreview?.entries?.length) {
      return;
    }

    const importableEntries = behaviorImportPreview.entries.filter((entry: any) =>
      entry?.canImport
      && String(entry?.matchedBehaviorKey || "").trim()
      && String(entry?.matchedBehaviorTitle || "").trim(),
    );

    if (!importableEntries.length) {
      toast.current?.show({
        severity: "warn",
        summary: "No entries ready to import",
        detail: "There are no confidently matched behavior entries to save yet.",
        life: 4000,
      });
      return;
    }

    setSavingImportedBehaviorEntries(true);
    try {
      await backendAPIStore.init();
      await Promise.all(importableEntries.map((entry: any) =>
        backendAPIStore.createResidentBehaviorLogEntry(residentId, {
          behaviorKey: entry.matchedBehaviorKey,
          behaviorTitle: entry.matchedBehaviorTitle,
          behaviorCategory: entry.matchedBehaviorCategory || "",
          residentPresentBehavior: entry.residentPresentBehavior || "",
          instructionsGiven: entry.instructionsGiven || "",
          residentResponse: entry.residentResponse || "",
          notes: entry.notes || "",
          startedAt: new Date(String(entry.startedAtLocal || "")).toISOString(),
          endedAt: new Date(String(entry.startedAtLocal || "")).toISOString(),
          isOngoing: false,
          status: "resolved",
        }),
      ));
      await reloadBehaviorLogEntries({ resetPagination: true });
      setActiveSection("recent");
      clearBehaviorImportState();
      toast.current?.show({
        severity: "success",
        summary: "Behavior entries imported",
        detail: `${importableEntries.length} daily behavior log entr${importableEntries.length === 1 ? "y" : "ies"} saved successfully.`,
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to import the previewed behavior entries.",
        life: 5000,
      });
    } finally {
      setSavingImportedBehaviorEntries(false);
    }
  };

  const updateBehaviorImportPreviewEntry = (previewId: string, behaviorKey: string) => {
    const selectedBehaviorOption = behaviorOptions.find((option) => option.behaviorKey === behaviorKey);
    setBehaviorImportPreview((previousState: any) => {
      if (!previousState?.entries?.length) {
        return previousState;
      }

      const nextEntries = previousState.entries.map((entry: any) => {
        if (entry?.previewId !== previewId) {
          return entry;
        }

        if (!selectedBehaviorOption) {
          return {
            ...entry,
            canImport: false,
            matchedBehaviorKey: "",
            matchedBehaviorTitle: "",
            matchedBehaviorCategory: "",
            instructionsGiven: "",
            warnings: ["Choose the correct care-plan behavior to continue importing this entry."],
            manuallyReviewed: false,
          };
        }

        return {
          ...entry,
          canImport: false,
          matchedBehaviorKey: selectedBehaviorOption.behaviorKey,
          matchedBehaviorTitle: selectedBehaviorOption.behaviorTitle,
          matchedBehaviorCategory: selectedBehaviorOption.sectionCategory || "",
          instructionsGiven: selectedBehaviorOption.instructionSuggestions?.[0]
            || entry?.instructionsGiven
            || "",
          warnings: ["Confirm this behavior selection before importing this entry."],
          manuallyReviewed: true,
        };
      });

      return {
        ...previousState,
        entries: nextEntries,
      };
    });
  };

  const confirmBehaviorImportPreviewEntry = (previewId: string) => {
    setBehaviorImportPreview((previousState: any) => {
      if (!previousState?.entries?.length) {
        return previousState;
      }

      const nextEntries = previousState.entries.map((entry: any) => {
        if (entry?.previewId !== previewId) {
          return entry;
        }

        if (!String(entry?.matchedBehaviorKey || "").trim()) {
          return entry;
        }

        return {
          ...entry,
          canImport: true,
          warnings: [],
          manuallyReviewed: true,
        };
      });

      return {
        ...previousState,
        entries: nextEntries,
      };
    });
  };

  const openProviderOneIdSetup = () => {
    setProviderOneIdDraft(String(currentUserProfile?.providerOneId || "").trim());
    setShowProviderOneIdDialog(true);
  };

  const openResidentDobSetup = (dateOfBirth?: string) => {
    setResidentDobDraft(normalizeDateInputValue(String(dateOfBirth || residentProfileData?.dateOfBirth || "").trim()));
    setShowResidentDobDialog(true);
  };

  const saveProviderOneIdAndContinue = async () => {
    const normalizedProviderOneId = normalizePlainText(providerOneIdDraft, { maxLength: 40 }).trim();
    if (!normalizedProviderOneId) {
      toast.current?.show({
        severity: "warn",
        summary: "ProviderOne ID required",
        detail: "Enter your ProviderOne ID to continue with Behavior Tracker report generation.",
        life: 4000,
      });
      return;
    }

    if (!currentUserProfile?.userId) {
      toast.current?.show({
        severity: "error",
        summary: "Profile unavailable",
        detail: "Unable to save ProviderOne ID right now. Please open your profile and try again.",
        life: 4000,
      });
      return;
    }

    setSavingProviderOneId(true);
    try {
      await backendAPIStore.init();
      const updatedProfile = await backendAPIStore.updateUserProfile({
        ...currentUserProfile,
        providerOneId: normalizedProviderOneId,
      });
      setCurrentUserProfile(updatedProfile || { ...currentUserProfile, providerOneId: normalizedProviderOneId });
      setProviderOneIdDraft(normalizedProviderOneId);
      setReportProviderOneId(normalizedProviderOneId);
      setShowProviderOneIdDialog(false);
      void generateBehaviorLogReport(true, updatedProfile || { ...currentUserProfile, providerOneId: normalizedProviderOneId }, false, undefined, pendingReportOutputMode, behaviorReportIncludesTierDetails);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save ProviderOne ID.",
        life: 4000,
      });
    } finally {
      setSavingProviderOneId(false);
    }
  };

  const saveResidentDobAndContinue = async (profileOverride?: any) => {
    const normalizedResidentDob = String(residentDobDraft || "").trim();
    if (!normalizedResidentDob) {
      toast.current?.show({
        severity: "warn",
        summary: "Resident DOB required",
        detail: "Enter the resident's date of birth to continue with Behavior Tracker report generation.",
        life: 4000,
      });
      return;
    }

    const effectiveResidentProfile = profileOverride || residentProfileData;
    if (!effectiveResidentProfile?.residentId) {
      toast.current?.show({
        severity: "error",
        summary: "Resident profile unavailable",
        detail: "Unable to save the resident's date of birth right now.",
        life: 4000,
      });
      return;
    }

    setSavingResidentDob(true);
    try {
      await backendAPIStore.init();
      const updatedResidentProfile = await backendAPIStore.updateResident({
        ...effectiveResidentProfile,
        dateOfBirth: normalizedResidentDob,
      });
      const nextResidentProfile = updatedResidentProfile || { ...effectiveResidentProfile, dateOfBirth: normalizedResidentDob };
      setResidentProfileData(nextResidentProfile);
      setResidentDobDraft(normalizedResidentDob);
      setReportResidentDob(normalizedResidentDob);
      setShowResidentDobDialog(false);
      void generateBehaviorLogReport(true, profileOverride || currentUserProfile, true, nextResidentProfile, pendingReportOutputMode, behaviorReportIncludesTierDetails);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save the resident's date of birth.",
        life: 4000,
      });
    } finally {
      setSavingResidentDob(false);
    }
  };

  const buildBehaviorReportHtml = (
    dataset: any,
    options: {
      providerOneId: string;
      residentDob: string;
      printedName: string;
      signatureDate: string;
      includeTierDetails: boolean;
    },
  ) => {
    const packets = Array.isArray(dataset?.packets) ? dataset.packets : [];
    return `
      <html>
        <head>
          <title>Behavior Tracker - ${escapeBehaviorReportHtml(residentName)}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; color: #0f172a; background: #ffffff; }
            .page { max-width: 980px; margin: 0 auto; padding: 24px; }
            h1 { margin: 0 0 0.35rem; font-size: 1.8rem; color: #0f172a; }
            h2 { margin: 0 0 12px; color: #0f172a; font-size: 1.15rem; }
            h3 { margin: 18px 0 8px; color: #0f172a; font-size: 1rem; }
            p { color: #334155; }
            .range-label { color: #64748b; margin-bottom: 1rem; }
            .summary-table-wrap { margin-top: 12px; }
            .summary-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
            .summary-table th, .summary-table td { border: 1px solid #cbd5e1; padding: 10px 12px; vertical-align: top; text-align: left; }
            .summary-table th { width: 18%; background: #f8fafc; color: #0f172a; font-weight: 700; }
            .summary-table td { width: 32%; color: #0f172a; word-break: break-word; }
            .content { margin-top: 18px; }
            .packet-section { margin-top: 28px; page-break-before: always; }
            .packet-section:first-of-type { page-break-before: auto; }
            .week-block { margin-top: 18px; }
            .week-summary-header { margin-top: 12px; font-size: 12px; font-weight: 700; color: #0f172a; border: 1px solid #cbd5e1; border-bottom: none; background: #f8fafc; padding: 10px 12px; }
            .week-summary { font-size: 12px; line-height: 1.6; border: 1px solid #cbd5e1; border-top: none; background: #ffffff; padding: 12px; }
            .week-summary h4 { margin: 10px 0 6px; font-size: 13px; color: #0f172a; }
            .week-summary p { margin: 0 0 8px; color: #334155; }
            .week-summary ul { margin: 0 0 10px 18px; padding: 0; }
            .week-summary li { margin-bottom: 4px; }
            .attestation-section { margin-top: 28px; border: 1px solid #cbd5e1; padding: 16px; background: #fff; }
            .tier-section { margin-top: 28px; border: 1px solid #cbd5e1; padding: 16px; background: #fff; }
            .tier-section h2 { margin: 0 0 10px; color: #0f172a; }
            .tier-section ul { margin: 10px 0 0 18px; padding: 0; color: #334155; }
            .tier-section li { margin-bottom: 4px; }
            .attestation-section h2 { margin: 0 0 10px; color: #0f172a; }
            .attestation-note { margin: 0 0 18px; color: #334155; line-height: 1.6; }
            .signature-grid { display: grid; grid-template-columns: repeat(3, minmax(180px, 1fr)); gap: 14px; }
            .signature-item { border: 1px solid #cbd5e1; padding: 12px; background: #ffffff; min-height: 72px; }
            .signature-label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; margin-bottom: 10px; }
            .signature-value { color: #0f172a; min-height: 20px; }
            .signature-line { height: 24px; border-bottom: 1px solid #94a3b8; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px 10px; vertical-align: top; text-align: left; font-size: 12px; }
            th { background: #f8fafc; }
            .behavior-table-block + .behavior-table-block { margin-top: 16px; }
            .behavior-table-label { color: #9a3412; font-size: 12px; font-weight: 700; margin-top: 10px; }
            .report-footer { margin-top: 22px; text-align: center; color: #64748b; font-size: 0.86rem; }
            .report-footer a { color: #c96a16; font-weight: 700; text-decoration: underline; }
            @media print {
              body { background: #ffffff; }
              .page { max-width: none; padding: 0; }
            }
            @media (max-width: 720px) {
              .summary-table, .summary-table tbody, .summary-table tr, .summary-table th, .summary-table td { display: block; width: 100%; }
              .summary-table tr { border-bottom: 1px solid #cbd5e1; }
              .summary-table tr:last-child { border-bottom: none; }
              .summary-table th, .summary-table td { border-bottom: none; padding-bottom: 6px; }
              .summary-table td { padding-top: 0; padding-bottom: 12px; }
              .signature-grid { grid-template-columns: 1fr; }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <h1>Behavior Tracker Report</h1>
            <div class="range-label">${escapeBehaviorReportHtml(String(dataset?.monthLabel || selectedCbhsMonth))}</div>
            <div class="summary-table-wrap">
              <table class="summary-table" aria-label="Behavior report summary">
                <tbody>
                  <tr>
                    <th>Resident</th>
                    <td>${escapeBehaviorReportHtml(String(dataset?.resident?.name || residentName))}</td>
                    <th>ProviderOne ID</th>
                    <td>${escapeBehaviorReportHtml(options.providerOneId)}</td>
                  </tr>
                  <tr>
                    <th>Resident DOB</th>
                    <td>${escapeBehaviorReportHtml(options.residentDob)}</td>
                    <th>Facility</th>
                    <td>${escapeBehaviorReportHtml(String(dataset?.facility?.name || ""))}</td>
                  </tr>
                  <tr>
                    <th>Facility Address</th>
                    <td>${escapeBehaviorReportHtml(String(dataset?.facility?.address || ""))}</td>
                    <th>Distinct Behaviors</th>
                    <td>${escapeBehaviorReportHtml(String(dataset?.summary?.distinctBehaviorCount || 0))}</td>
                  </tr>
                  <tr>
                    <th>${String(dataset?.reportRangeKey || "monthly").trim() === "monthly" ? "Monthly Total Hours" : "Total Hours"}</th>
                    <td>${escapeBehaviorReportHtml(formatBehaviorHours(dataset?.summary?.totalBehaviorHours))}</td>
                    <th>Average Hours Per Day</th>
                    <td>${escapeBehaviorReportHtml(formatBehaviorHours(dataset?.summary?.averageBehaviorHoursPerDay))}</td>
                  </tr>
                  ${options.includeTierDetails ? `
                  <tr>
                    <th>Behavior Tier</th>
                    <td>${escapeBehaviorReportHtml(String(dataset?.summary?.behaviorTier || "Not Tiered"))}</td>
                  </tr>
                  ` : ""}
                </tbody>
              </table>
            </div>
            <div class="content">
              ${packets.length > 0
        ? packets.map((packet: any) => buildCbhsBehaviorPacketHtml(packet)).join("")
        : `<p>No documented behavior entries were found for ${escapeBehaviorReportHtml(String(dataset?.monthLabel || selectedCbhsMonth))}.</p>`
      }
              ${options.includeTierDetails ? `
              <section class="tier-section">
                <h2>Behavior Tier Reference</h2>
                <p>Washington Health Care Authority (HCA) tier reference based on average behavior-support hours per day for the selected report range.</p>
                <ul>
                  ${CBHS_TIER_LEGEND.map((item) => `<li>${escapeBehaviorReportHtml(item)}</li>`).join("")}
                </ul>
              </section>
              ` : ""}
              <section class="attestation-section">
                <h2>Attestation and Signature</h2>
                <p class="attestation-note">A representative of the organization must sign each form attesting to the accuracy of the information.</p>
                <div class="signature-grid">
                  <div class="signature-item">
                    <span class="signature-label">Printed Name</span>
                    <div class="signature-value">${escapeBehaviorReportHtml(options.printedName)}</div>
                  </div>
                  <div class="signature-item">
                    <span class="signature-label">Signature</span>
                    <div class="signature-line"></div>
                  </div>
                  <div class="signature-item">
                    <span class="signature-label">Date of Signature</span>
                    <div class="signature-value">${escapeBehaviorReportHtml(options.signatureDate)}</div>
                  </div>
                </div>
              </section>
              <div class="report-footer">
                Generated by AFH Hub.
                <a href="https://www.afhhub.com/" target="_blank" rel="noopener noreferrer">www.afhhub.com</a>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const printBehaviorReportHtml = (html: string) => {
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);
    printFrame.onload = () => {
      window.setTimeout(() => {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
      }, 50);
      window.setTimeout(() => {
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
      }, 1500);
    };
    printFrame.srcdoc = html;
  };

  const generateBehaviorLogReport = async (
    skipProviderOneIdCheck: boolean = false,
    profileOverride?: any,
    skipResidentDobCheck: boolean = false,
    residentProfileOverride?: any,
    outputMode: "print" | "view" = "view",
    includeTierDetails: boolean = true,
  ) => {
    setPendingReportOutputMode(outputMode);
    const effectiveProfile = profileOverride || currentUserProfile;
    const normalizedProviderOneId = String(reportProviderOneId || effectiveProfile?.providerOneId || "").trim();
    if (!skipProviderOneIdCheck && !normalizedProviderOneId) {
      setProviderOneIdDraft(normalizedProviderOneId);
      openProviderOneIdSetup();
      return;
    }

    if (!residentId || (selectedBehaviorReportRange === "monthly" && !selectedCbhsMonth)) {
      return;
    }

    setGeneratingCbhsReport(true);
    try {
      await backendAPIStore.init();
      let nextProfile = effectiveProfile;
      if (
        normalizedProviderOneId
        && normalizedProviderOneId !== String(effectiveProfile?.providerOneId || "").trim()
        && effectiveProfile?.userId
      ) {
        const updatedProfile = await backendAPIStore.updateUserProfile({
          ...effectiveProfile,
          providerOneId: normalizedProviderOneId,
        });
        nextProfile = updatedProfile || { ...effectiveProfile, providerOneId: normalizedProviderOneId };
        setCurrentUserProfile(nextProfile);
        setProviderOneIdDraft(normalizedProviderOneId);
        setReportProviderOneId(normalizedProviderOneId);
      }

      const freshDataset = await backendAPIStore.getResidentBehaviorLogCbhsMonthlyDataset(residentId, {
        reportRange: selectedBehaviorReportRange,
        reportMonth: selectedBehaviorReportRange === "monthly" ? selectedCbhsMonth : undefined,
        behaviorHoursMode,
      });
      const resolvedResidentDob = String(
        reportResidentDob
        || freshDataset?.resident?.dateOfBirth
        || residentProfileOverride?.dateOfBirth
        || residentProfileData?.dateOfBirth
        || "",
      ).trim();
      if (!skipResidentDobCheck && !resolvedResidentDob) {
        openResidentDobSetup(freshDataset?.resident?.dateOfBirth);
        return;
      }
      let nextResidentProfile = residentProfileOverride || residentProfileData;
      if (
        resolvedResidentDob
        && resolvedResidentDob !== String(nextResidentProfile?.dateOfBirth || "").trim()
        && nextResidentProfile?.residentId
      ) {
        const updatedResidentProfile = await backendAPIStore.updateResident({
          ...nextResidentProfile,
          dateOfBirth: resolvedResidentDob,
        });
        nextResidentProfile = updatedResidentProfile || { ...nextResidentProfile, dateOfBirth: resolvedResidentDob };
        setResidentProfileData(nextResidentProfile);
        setResidentDobDraft(resolvedResidentDob);
        setReportResidentDob(resolvedResidentDob);
      }
      const dataset = applyCbhsSummaryDrafts(freshDataset, cbhsWeekSummaryDrafts);
      const resolvedPrintedName = String(reportPrintedName || getPrintableUserName(nextProfile)).trim();
      const resolvedSignatureDate = String(reportSignatureDate || getDefaultDateInputValue()).trim();
      const reportHtml = buildBehaviorReportHtml(dataset, {
        providerOneId: normalizedProviderOneId,
        residentDob: resolvedResidentDob,
        printedName: resolvedPrintedName,
        signatureDate: resolvedSignatureDate,
        includeTierDetails,
      });
      setBehaviorReportIncludesTierDetails(includeTierDetails);
      if (outputMode === "view") {
        setBehaviorReportPreviewHtml(reportHtml);
        setShowBehaviorReportPreviewDialog(true);
        return;
      }

      if (showBehaviorReportPreviewDialog && behaviorReportPreviewFrameRef.current?.contentWindow) {
        behaviorReportPreviewFrameRef.current.contentWindow.focus();
        behaviorReportPreviewFrameRef.current.contentWindow.print();
        return;
      }

      printBehaviorReportHtml(reportHtml);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the Behavior Tracker report.",
        life: 4000,
      });
    } finally {
      setGeneratingCbhsReport(false);
    }
  };

  const hasDraftBehaviorEntries = behaviorLogEntries.some((entry: any) =>
    String(entry?.status || "").trim().toLowerCase() === "draft",
  );
  const importPreviewEntriesList = Array.isArray(behaviorImportPreview?.entries) ? behaviorImportPreview.entries : [];
  const readyToImportCount = importPreviewEntriesList.filter((entry: any) => entry?.canImport).length;
  const reviewRequiredCount = importPreviewEntriesList.filter((entry: any) => !entry?.canImport).length;

  if (userRoleType && userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName="Daily Behavior Log"
        onHide={() => setShowSubscriptionDialog(false)}
      />
      <Dialog
        visible={showMissingCarePlanDialog}
        onHide={() => setShowMissingCarePlanDialog(false)}
        header="Manage Care Plan First"
        style={{ width: "min(96vw, 34rem)" }}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              rounded
              outlined
              severity="secondary"
              onClick={() => setShowMissingCarePlanDialog(false)}
            />
            {canOpenManageCarePlan ? (
              <Button
                type="button"
                label="Go to Manage Care Plan"
                rounded
                severity="warning"
                onClick={() => {
                  setShowMissingCarePlanDialog(false);
                  navigate(RouterAddress.getResidentNCPPagePath(String(residentProfileData?.homeId || ""), String(residentId || "")));
                }}
              />
            ) : null}
          </div>
        )}
      >
        <div style={{ color: "#475569", lineHeight: 1.7 }}>
          Upload the assessment first, then create a care plan in Manage Care Plan before using Behavior Tracker.
        </div>
      </Dialog>
      {residentId ? (
        <ResidentDailyLogImportDialog
          visible={showSharedDailyLogImportDialog}
          residentId={residentId}
          behaviorOptions={behaviorOptions}
          onHide={() => setShowSharedDailyLogImportDialog(false)}
          onImportCompleted={async () => {
            await reloadBehaviorLogEntries({ resetPagination: true });
          }}
        />
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div
          className="flex justify-content-between align-items-start gap-3 flex-wrap"
          style={{ width: "100%" }}
        >
          <div style={{ minWidth: 0 }}>
            {renderSurfaceHeader(
              `${residentName} - Daily Behavior Log`,
              "Write down behaviors, support given, and follow-up notes for the day.",
              "pi pi-clock",
            )}
          </div>
          {!loading && carePlanData ? (
            <div className="flex align-items-center justify-content-end gap-2 flex-wrap">
              {!archivedReadOnly && activeSection !== "composer" ? (
                <Button
                  type="button"
                  label="New Behavior Entry"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => {
                    if (!hasBehaviorRows) {
                      setShowMissingCarePlanDialog(true);
                      return;
                    }
                    setActiveSection("composer");
                  }}
                />
              ) : null}
              {activeSection !== "monthly" ? (
                <Button
                  type="button"
                  label="Generate Reports"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setActiveSection("monthly")}
                />
              ) : null}
              {!archivedReadOnly && activeSection !== "recent" ? (
                <Button
                  type="button"
                  label="Recent Entries"
                  severity="warning"
                  rounded
                  outlined
                  onClick={() => setActiveSection("recent")}
                />
              ) : null}
              {hasDraftBehaviorEntries ? (
                !archivedReadOnly && activeSection !== "drafts" ? (
                  <Button
                    type="button"
                    label="Draft Reports"
                    severity="warning"
                    rounded
                    outlined
                    onClick={() => setActiveSection("drafts")}
                  />
                ) : null
              ) : null}
              <Button
                type="button"
                label="Add Previous Behavior Logs"
                icon="pi pi-upload"
                severity="warning"
                rounded
                outlined
                onClick={() => setShowSharedDailyLogImportDialog(true)}
              />
            </div>
          ) : null}
        </div>
        {loading ? (
          <div className="card flex justify-content-center p-5">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : !isProviderSubscribed ? (
          <div style={{ ...appInnerCardStyle, maxWidth: 760, margin: "0 auto", padding: "1rem 1.1rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Subscription required</div>
            <div className="mt-2" style={appMutedTextStyle}>
              Daily Behavior Log is available for subscribed providers only.
            </div>
          </div>
        ) : (
          <>
            {!carePlanData ? (
              <div style={appInnerCardStyle}>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>Daily Behavior Log</div>
                <div className="mt-2" style={appMutedTextStyle}>
                  Unable to load resident behavior details right now.
                </div>
              </div>
            ) : (
              <>
                {activeSection === "monthly" ? (
                  <>
                    <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
                      <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                        <div>
                          <div style={{ fontWeight: 800, color: "#0f172a" }}>Behavior Report</div>
                          <div className="mt-1" style={appMutedTextStyle}>
                            Choose a report range to build the Behavior Tracker report from daily entries.
                          </div>
                        </div>
                        <div className="flex align-items-end gap-2 flex-wrap">
                          <label style={{ display: "grid", gap: "0.35rem", minWidth: "24rem" }}>
                            <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Report Range</span>
                            <SelectButton
                              value={selectedBehaviorReportRange}
                              onChange={(event) => setSelectedBehaviorReportRange(event.value)}
                              options={BEHAVIOR_REPORT_RANGE_OPTIONS as any}
                              optionLabel="label"
                              optionValue="value"
                              allowEmpty={false}
                            />
                          </label>
                          {selectedBehaviorReportRange === "monthly" ? (
                            <label style={{ display: "grid", gap: "0.35rem", minWidth: "15rem" }}>
                              <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Report Month</span>
                              <Calendar
                                value={getCbhsMonthDateValue(selectedCbhsMonth)}
                                onChange={(event) => setSelectedCbhsMonth(getCbhsMonthKeyFromDateValue(event.value))}
                                view="month"
                                dateFormat="MM yy"
                                showIcon
                                readOnlyInput
                                className="w-full"
                                inputClassName="w-full"
                              />
                            </label>
                          ) : null}
                          <Button
                            type="button"
                            label="View Report"
                            icon="pi pi-eye"
                            severity="warning"
                            rounded
                            outlined
                            onClick={() => { void generateBehaviorLogReport(false, undefined, false, undefined, "view", behaviorReportIncludesTierDetails); }}
                            disabled={loadingCbhsPreview || !cbhsPreviewDataset || Number(cbhsPreviewDataset?.summary?.sourceEntryCount || 0) === 0}
                          />
                          <Button
                            type="button"
                            label="Print Report"
                            icon="pi pi-print"
                            severity="warning"
                            rounded
                            onClick={() => { void generateBehaviorLogReport(false, undefined, false, undefined, "print", behaviorReportIncludesTierDetails); }}
                            disabled={loadingCbhsPreview || !cbhsPreviewDataset || Number(cbhsPreviewDataset?.summary?.sourceEntryCount || 0) === 0}
                          />
                        </div>
                      </div>
                      {generatingCbhsReport ? (
                        <div className="mt-2" style={appMutedTextStyle}>
                          Preparing the Behavior Tracker report...
                        </div>
                      ) : null}
                    </div>
                    <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>Report Data</div>
                      <div className="mt-1" style={appMutedTextStyle}>
                        These details will appear in the report.
                      </div>
                      <div className="grid mt-3">
                        <div className="col-12 md:col-6">
                          <label style={{ display: "grid", gap: "0.35rem" }}>
                            <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Provider One ID</span>
                            <InputText
                              value={reportProviderOneId}
                              onChange={(event) => setReportProviderOneId(event.target.value)}
                              maxLength={40}
                              className="w-full"
                            />
                          </label>
                        </div>
                        <div className="col-12 md:col-6">
                          <label style={{ display: "grid", gap: "0.35rem" }}>
                            <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Resident DOB</span>
                            <input
                              type="date"
                              value={reportResidentDob}
                              onChange={(event) => setReportResidentDob(event.target.value)}
                              className="p-inputtext p-component w-full"
                            />
                          </label>
                        </div>
                        <div className="col-12">
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem", marginBottom: "0.75rem" }}>
                            Tier Details
                          </div>
                          <div className="flex align-items-start gap-2">
                            <Checkbox
                              inputId="behavior-report-include-tier-details"
                              checked={behaviorReportIncludesTierDetails}
                              onChange={(event) => setBehaviorReportIncludesTierDetails(Boolean(event.checked))}
                            />
                            <label
                              htmlFor="behavior-report-include-tier-details"
                              style={{ color: "#475569", lineHeight: 1.6, cursor: "pointer" }}
                            >
                              Include resident tier details in this report, as calculated by Hub.
                            </label>
                          </div>
                        </div>
                        <div className="col-12">
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem", marginBottom: "0.75rem" }}>
                            Multiple Behavior Hours
                          </div>
                          <div className="flex flex-column gap-2">
                            <div className="flex align-items-start gap-2">
                              <Checkbox
                                inputId="behavior-report-common-hours"
                                checked={behaviorHoursMode === "common"}
                                onChange={() => setBehaviorHoursMode("common")}
                              />
                              <label htmlFor="behavior-report-common-hours" style={{ color: "#475569", lineHeight: 1.6, cursor: "pointer" }}>
                                Treat multiple behaviors as one common time (default). Count the entry once in the overall total.
                              </label>
                            </div>
                            <div className="flex align-items-start gap-2">
                              <Checkbox
                                inputId="behavior-report-separate-hours"
                                checked={behaviorHoursMode === "separate"}
                                onChange={() => setBehaviorHoursMode("separate")}
                              />
                              <label htmlFor="behavior-report-separate-hours" style={{ color: "#475569", lineHeight: 1.6, cursor: "pointer" }}>
                                Count hours separately for each behavior in the behavior-level breakdown.
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem", marginBottom: "0.75rem" }}>
                            Signature Section
                          </div>
                        </div>
                        <div className="col-12 md:col-6">
                          <label style={{ display: "grid", gap: "0.35rem" }}>
                            <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Printed Name</span>
                            <InputText
                              value={reportPrintedName}
                              onChange={(event) => setReportPrintedName(event.target.value)}
                              className="w-full"
                            />
                          </label>
                        </div>
                        <div className="col-12 md:col-6">
                          <label style={{ display: "grid", gap: "0.35rem" }}>
                            <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Date of Signature</span>
                            <input
                              type="date"
                              value={reportSignatureDate}
                              onChange={(event) => setReportSignatureDate(event.target.value)}
                              className="p-inputtext p-component w-full"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
                      <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                        <div style={appMutedTextStyle}>
                          Review each week and update the summary before you export the report.
                        </div>
                        {loadingCbhsPreview ? (
                          <div style={appMutedTextStyle}>Refreshing report...</div>
                        ) : null}
                      </div>

                      {cbhsPreviewDataset ? (
                        <>
                          {(cbhsPreviewDataset?.packets || []).length > 0 ? (
                            <div className="mt-3 flex flex-column gap-3">
                              {(cbhsPreviewDataset?.packets || []).map((packet: any) => (
                                <div
                                  key={`packet-${packet?.packetIndex}`}
                                  style={{
                                    display: "grid",
                                    gap: "1rem",
                                    justifyItems: "center",
                                  }}
                                >
                                  {(packet?.weeks || []).map((week: any) => {
                                    const summaryKey = getCbhsWeekSummaryKey(
                                      getCbhsWeekSummaryScope(cbhsPreviewDataset),
                                      Number(packet?.packetIndex || 0),
                                      Number(week?.weekNumber || 0),
                                    );
                                    return (
                                      <div
                                        key={summaryKey}
                                        style={{
                                          width: "100%",
                                          maxWidth: "52rem",
                                          padding: "0.9rem 1rem",
                                          borderRadius: "14px",
                                          border: "1px solid #fed7aa",
                                          background: "#fffaf0",
                                        }}
                                      >
                                        <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                                          <div style={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.5 }}>
                                            Weekly {week?.weekNumber || ""} Summary: Note behaviors, unusual events, interventions, and whether they were successful.
                                          </div>
                                          <div style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                            {(week?.days || []).filter((day: any) =>
                                              Array.isArray(day?.behaviorOccurrences) && day.behaviorOccurrences.some((occurrence: any) => Number(occurrence?.count || 0) > 0)
                                            ).length} active day(s)
                                          </div>
                                        </div>
                                        <div
                                          className="mt-2"
                                          contentEditable
                                          suppressContentEditableWarning
                                          onInput={(event) => {
                                            updateCbhsWeekSummaryDraft(
                                              summaryKey,
                                              event.currentTarget?.innerHTML || "",
                                              setCbhsWeekSummaryDrafts,
                                            );
                                          }}
                                          onBlur={(event) => {
                                            updateCbhsWeekSummaryDraft(
                                              summaryKey,
                                              event.currentTarget?.innerHTML || "",
                                              setCbhsWeekSummaryDrafts,
                                            );
                                          }}
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeRichHtml(cbhsWeekSummaryDrafts[summaryKey] ?? String(week?.summary || "")),
                                          }}
                                          style={{
                                            minHeight: "12rem",
                                            padding: "0.9rem 1rem",
                                            borderRadius: "14px",
                                            border: "1px solid #fed7aa",
                                            background: "#ffffff",
                                            color: "#0f172a",
                                            lineHeight: 1.6,
                                            outline: "none",
                                            boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.04)",
                                          }}
                                        />
                                        <div className="mt-3 flex justify-content-end">
                                          <Button
                                            type="button"
                                            label="Save Weekly Summary"
                                            icon="pi pi-check"
                                            severity="warning"
                                            rounded
                                            outlined
                                            onClick={() => saveWeeklySummaryDraft(summaryKey)}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-3" style={appMutedTextStyle}>
                              No daily behavior entries were found for this month yet.
                            </div>
                          )}
                        </>
                      ) : !loadingCbhsPreview ? (
                        <div className="mt-3" style={appMutedTextStyle}>
                          Choose a month to load the report.
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : null}

                {activeSection === "import" ? (
                  <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
                    <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                      <div>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>Add Previous Behavior Logs</div>
                        <div className="mt-1" style={appMutedTextStyle}>
                          Upload a progress notes file to review suggested daily behavior entries before saving them.
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          label={selectedImportFile ? "Change File" : "Choose File"}
                          icon="pi pi-upload"
                          rounded
                          outlined
                          onClick={() => importFileInputRef.current?.click()}
                        />
                        <Button
                          type="button"
                          label="Preview Import"
                          icon="pi pi-search"
                          severity="warning"
                          rounded
                          onClick={() => { void previewBehaviorImport(); }}
                          disabled={!selectedImportFile || previewingImportFile || savingImportedBehaviorEntries}
                          loading={previewingImportFile}
                        />
                      </div>
                    </div>
                    <input
                      ref={importFileInputRef}
                      type="file"
                      accept={DAILY_BEHAVIOR_IMPORT_ACCEPT}
                      style={{ display: "none" }}
                      onChange={handleImportFileSelection}
                    />
                    <div className="mt-3" style={{ ...appMutedTextStyle, fontSize: "0.9rem" }}>
                      Supported files: DOC, DOCX, TXT. Best results come from DOCX or TXT exports that include Date, Staff on Duty, and time-stamped notes.
                    </div>
                    {selectedImportFile ? (
                      <div
                        className="mt-3"
                        style={{
                          padding: "0.85rem 1rem",
                          borderRadius: "14px",
                          border: "1px solid #cbd5e1",
                          background: "#f8fafc",
                          color: "#0f172a",
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{selectedImportFile.name}</div>
                        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                          {(selectedImportFile.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    ) : null}
                    {behaviorImportPreview ? (
                      <div className="mt-4" style={{ display: "grid", gap: "1rem" }}>
                        <div
                          style={{
                            padding: "1rem",
                            borderRadius: "16px",
                            border: "1px solid #fed7aa",
                            background: "#fffaf0",
                          }}
                        >
                          <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                            <div style={{ display: "grid", gap: "0.35rem" }}>
                              <div style={{ fontWeight: 800, color: "#0f172a" }}>Preview Summary</div>
                              <div style={appMutedTextStyle}>
                                {readyToImportCount} ready to import, {reviewRequiredCount} need review, from {behaviorImportPreview?.summary?.totalParsedEntries || 0} parsed entr{Number(behaviorImportPreview?.summary?.totalParsedEntries || 0) === 1 ? "y" : "ies"}.
                              </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <Button
                                type="button"
                                label="Clear Preview"
                                severity="secondary"
                                outlined
                                rounded
                                onClick={clearBehaviorImportState}
                                disabled={savingImportedBehaviorEntries}
                              />
                              <Button
                                type="button"
                                label="Import Matched Entries"
                                icon="pi pi-check"
                                severity="warning"
                                rounded
                                onClick={() => { void importPreviewEntries(); }}
                                loading={savingImportedBehaviorEntries}
                                disabled={previewingImportFile || savingImportedBehaviorEntries || !readyToImportCount}
                              />
                            </div>
                          </div>
                          {(behaviorImportPreview?.warnings || []).length > 0 ? (
                            <div className="mt-3" style={{ display: "grid", gap: "0.5rem" }}>
                              {(behaviorImportPreview.warnings || []).map((warning: string, index: number) => (
                                <div
                                  key={`warning-${index}`}
                                  style={{
                                    padding: "0.75rem 0.9rem",
                                    borderRadius: "12px",
                                    border: "1px solid #fdba74",
                                    background: "#fff7ed",
                                    color: "#9a3412",
                                  }}
                                >
                                  {warning}
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ display: "grid", gap: "0.9rem" }}>
                          {(behaviorImportPreview?.entries || []).map((entry: any) => (
                            <div
                              key={entry.previewId}
                              style={{
                                padding: "1rem",
                                borderRadius: "16px",
                                border: `1px solid ${entry?.canImport ? "#bbf7d0" : "#fecaca"}`,
                                background: entry?.canImport ? "#f0fdf4" : "#fff7f7",
                              }}
                            >
                              <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                                <div style={{ display: "grid", gap: "0.35rem" }}>
                                  <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                    {entry?.dateKey || "Unknown date"} at {entry?.timeLabel || "Unknown time"}
                                  </div>
                                  <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                    {entry?.staffOnDuty ? `Staff on duty: ${entry.staffOnDuty}` : "Staff on duty not found in file"}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    padding: "0.35rem 0.7rem",
                                    borderRadius: "999px",
                                    background: entry?.canImport ? "#dcfce7" : "#fee2e2",
                                    color: entry?.canImport ? "#166534" : "#991b1b",
                                    fontWeight: 700,
                                    fontSize: "0.82rem",
                                  }}
                                >
                                  {entry?.canImport ? "Ready To Import" : "Needs Review"}
                                </div>
                              </div>
                              <div className="mt-3" style={{ color: "#0f172a", lineHeight: 1.6 }}>
                                {entry?.noteText || ""}
                              </div>
                              <div className="mt-3" style={{ display: "grid", gap: "0.35rem" }}>
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                                  Matched behavior: {entry?.matchedBehaviorTitle || "No confident match"}
                                </div>
                                <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                  {entry?.matchedBehaviorCategory || "Review the behavior selection before importing."}
                                </div>
                                <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                  Instructions: {entry?.instructionsGiven || "No care-plan instruction available"}
                                </div>
                              </div>
                              <div className="mt-3" style={{ display: "grid", gap: "0.45rem" }}>
                                <label style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem" }}>
                                  Review and choose the correct behavior
                                </label>
                                {!entry?.canImport && entry?.matchedBehaviorTitle ? (
                                  <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", color: "#9a3412" }}>
                                    Recommended behavior: {entry.matchedBehaviorTitle}. We pre-selected it for you. Please confirm it or choose a different behavior.
                                  </div>
                                ) : null}
                                <Dropdown
                                  value={entry?.matchedBehaviorKey || ""}
                                  options={behaviorOptions.map((option) => ({
                                    label: option.sectionCategory
                                      ? `${option.behaviorTitle} (${option.sectionCategory})`
                                      : option.behaviorTitle,
                                    value: option.behaviorKey,
                                  }))}
                                  placeholder="Select a care-plan behavior"
                                  className="w-full"
                                  showClear
                                  onChange={(event) => updateBehaviorImportPreviewEntry(entry.previewId, String(event.value || ""))}
                                />
                                {entry?.manuallyReviewed ? (
                                  <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: "#166534" }}>
                                    {entry?.canImport
                                      ? "Behavior confirmed during review. This entry is now ready to import."
                                      : "Behavior selected during review. Confirm it to finish the review."}
                                  </div>
                                ) : null}
                                {!entry?.canImport && entry?.matchedBehaviorKey ? (
                                  <div
                                    style={{
                                      padding: "0.7rem 0.8rem",
                                      borderRadius: "12px",
                                      border: "1px solid #86efac",
                                      background: "#f0fdf4",
                                    }}
                                  >
                                    <Button
                                      type="button"
                                      label="Confirm Selected Behavior"
                                      icon="pi pi-check"
                                      severity="success"
                                      rounded
                                      onClick={() => confirmBehaviorImportPreviewEntry(entry.previewId)}
                                    />
                                  </div>
                                ) : null}
                              </div>
                              {(entry?.warnings || []).length > 0 ? (
                                <div className="mt-3" style={{ display: "grid", gap: "0.5rem" }}>
                                  {(entry.warnings || []).map((warning: string, index: number) => (
                                    <div
                                      key={`${entry.previewId}-warning-${index}`}
                                      style={{
                                        padding: "0.7rem 0.85rem",
                                        borderRadius: "12px",
                                        border: "1px solid #fecaca",
                                        background: "#fff1f2",
                                        color: "#9f1239",
                                      }}
                                    >
                                      {warning}
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <DailyBehaviorLogPanel
                  behaviorOptions={behaviorOptions}
                  entries={behaviorLogEntries}
                  loading={loadingBehaviorLogEntries}
                  saving={savingBehaviorLogEntry}
                  emptyMessage="No provider behavior entries yet for this resident."
                  onOpenCarePlan={() => {
                    navigate(RouterAddress.getResidentNCPPagePath(String(residentProfileData?.homeId || ""), String(residentId || "")));
                  }}
                  openCarePlanLabel="Go to Manage Care Plan"
                  collapsibleComposer
                  defaultComposerVisible
                  composerVisible={activeSection === "composer"}
                  onComposerVisibleChange={(visible) => {
                    if (visible) {
                      setActiveSection("composer");
                    }
                  }}
                  showComposerSection={activeSection === "composer"}
                  showComposerToggleButton={false}
                  collapsibleEntries
                  defaultEntriesVisible
                  entriesVisible={activeSection === "recent"}
                  onEntriesVisibleChange={(visible) => {
                    if (visible) {
                      setActiveSection("recent");
                    }
                  }}
                  showEntriesSection={activeSection === "recent"}
                  showEntriesToggleButton={false}
                  showDraftsSection={activeSection === "drafts"}
                  serverEntriesPage={behaviorLogPage}
                  serverEntriesPageSize={10}
                  serverEntriesHasPreviousPage={behaviorLogPage > 1}
                  serverEntriesHasNextPage={Boolean(behaviorLogNextToken)}
                  onEntriesPreviousPage={() => {
                    const previousPage = Math.max(1, behaviorLogPage - 1);
                    void reloadBehaviorLogEntries({
                      page: previousPage,
                      nextToken: behaviorLogPageTokens[previousPage - 1] || "",
                    });
                  }}
                  onEntriesNextPage={() => {
                    if (!behaviorLogNextToken) {
                      return;
                    }
                    const nextPage = behaviorLogPage + 1;
                    setBehaviorLogPageTokens((previousState) => {
                      const nextState = previousState.slice(0, nextPage);
                      nextState[nextPage - 1] = behaviorLogNextToken;
                      return nextState;
                    });
                    void reloadBehaviorLogEntries({
                      page: nextPage,
                      nextToken: behaviorLogNextToken,
                    });
                  }}
                  canEditEntry={() => !archivedReadOnly}
                  onFormatText={formatBehaviorLogText}
                  onGenerateCaregiverInterventions={generateCaregiverInterventions}
                  onCreateEntry={createBehaviorLogEntry}
                  onUpdateEntry={updateBehaviorLogEntry}
                  onDeleteDraft={deleteBehaviorLogDraft}
                  onDraftSaved={() => setActiveSection("drafts")}
                  prnMedicationOptions={prnMedicationOptions}
                  loadingPrnMedicationOptions={loadingPrnMedicationOptions}
                  allowRecentEntryReuse={allowRecentEntryReuse}
                />
              </>
            )}
          </>
        )}
      </div>
      <Dialog
        header={renderSurfaceHeader("Behavior Tracker Report", "Review the monthly report before printing.", "pi pi-file")}
        visible={showBehaviorReportPreviewDialog}
        modal
        maximizable
        style={{ width: "98vw", maxWidth: "98vw" }}
        contentStyle={{ height: "calc(100vh - 9rem)", padding: "0.75rem" }}
        onHide={() => setShowBehaviorReportPreviewDialog(false)}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              outlined
              rounded
              onClick={() => setShowBehaviorReportPreviewDialog(false)}
            />
            <Button
              type="button"
              label="Print Report"
              icon="pi pi-print"
              severity="warning"
              rounded
              onClick={() => { void generateBehaviorLogReport(true, currentUserProfile, true, residentProfileData, "print", behaviorReportIncludesTierDetails); }}
            />
          </div>
        )}
      >
        <iframe
          ref={behaviorReportPreviewFrameRef}
          title="Behavior Tracker report preview"
          srcDoc={behaviorReportPreviewHtml}
          style={{
            width: "100%",
            height: "100%",
            minHeight: "72vh",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            background: "#ffffff",
          }}
        />
      </Dialog>
      <Dialog
        header="ProviderOne ID Required"
        visible={showProviderOneIdDialog}
        style={{ width: "min(92vw, 34rem)" }}
        modal
        onHide={() => {
          if (!savingProviderOneId) {
            setShowProviderOneIdDialog(false);
          }
        }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              disabled={savingProviderOneId}
              onClick={() => setShowProviderOneIdDialog(false)}
            />
            <Button
              type="button"
              label="Save and Continue"
              severity="warning"
              rounded
              loading={savingProviderOneId}
              onClick={() => { void saveProviderOneIdAndContinue(); }}
            />
          </div>
        )}
      >
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={appMutedTextStyle}>
            ProviderOne ID is needed before you can generate the Behavior Tracker report. We'll save it to your profile so you only need to enter it once.
          </div>
          <span className="p-float-label">
            <InputText
              id="providerOneId"
              value={providerOneIdDraft}
              onChange={(event) => setProviderOneIdDraft(event.target.value)}
              maxLength={40}
              className="w-full"
            />
            <label htmlFor="providerOneId">ProviderOne ID</label>
          </span>
        </div>
      </Dialog>
      <Dialog
        header="Resident DOB Required"
        visible={showResidentDobDialog}
        style={{ width: "min(92vw, 34rem)" }}
        modal
        onHide={() => {
          if (!savingResidentDob) {
            setShowResidentDobDialog(false);
          }
        }}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              outlined
              rounded
              disabled={savingResidentDob}
              onClick={() => setShowResidentDobDialog(false)}
            />
            <Button
              type="button"
              label="Save and Continue"
              severity="warning"
              rounded
              loading={savingResidentDob}
              onClick={() => { void saveResidentDobAndContinue(); }}
            />
          </div>
        )}
      >
        <div style={{ display: "grid", gap: "0.9rem" }}>
          <div style={appMutedTextStyle}>
            Resident date of birth is needed before you can generate the Behavior Tracker report. We'll save it to the resident profile so you only need to enter it once.
          </div>
          <label style={{ display: "grid", gap: "0.4rem" }}>
            <span style={{ fontWeight: 700, color: "#0f172a" }}>Resident Date of Birth</span>
            <input
              type="date"
              value={residentDobDraft}
              onChange={(event) => setResidentDobDraft(event.target.value)}
              className="p-inputtext p-component w-full"
            />
          </label>
        </div>
      </Dialog>
    </div>
  );
};

export default ProviderDailyBehaviorLogPage;
